import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  Microphone, Stop, Trash, BookBookmark, CircleNotch, Calendar,
  PaperPlaneRight, Pencil, MapPin, Info,
} from "@phosphor-icons/react";
import {
  transcribeAudio, createJournalEntry, listJournalEntries, deleteJournalEntry,
} from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("de-DE", {
    day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit",
  });
}

function pickMime() {
  const cands = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  for (const m of cands) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

function Recorder({ onTranscribed }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const startedRef = useRef(0);
  const intervalRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickMime();
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
        const blob = new Blob(chunksRef.current, { type: mimeType || "audio/webm" });
        if (blob.size < 1000) {
          toast.error("Aufnahme zu kurz");
          return;
        }
        setBusy(true);
        try {
          const r = await transcribeAudio(blob, "de");
          onTranscribed(r.text || "");
          toast.success("Transkribiert ✨");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Transkription fehlgeschlagen");
        } finally {
          setBusy(false);
        }
      };
      rec.start();
      recRef.current = rec;
      startedRef.current = Date.now();
      setElapsed(0);
      setRecording(true);
      intervalRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedRef.current) / 1000));
      }, 250);
    } catch (err) {
      toast.error("Mikrofon-Zugriff verweigert. Bitte Browser-Berechtigungen prüfen.");
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch { /* ignore */ }
    if (intervalRef.current) clearInterval(intervalRef.current);
    setRecording(false);
  };

  useEffect(() => () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  return (
    <div className="flex items-center gap-3" data-testid="journal-recorder">
      {!recording && !busy && (
        <button
          onClick={start}
          data-testid="journal-record-start"
          className="bg-[#00FA9A] text-black hover:bg-[#33FBAE] rounded-full px-5 py-3 flex items-center gap-2"
        >
          <Microphone size={18} weight="fill" /> Aufnahme starten
        </button>
      )}
      {recording && (
        <button
          onClick={stop}
          data-testid="journal-record-stop"
          className="bg-[#FF5500] text-white hover:bg-[#9A4530] rounded-full px-5 py-3 flex items-center gap-2"
        >
          <Stop size={18} weight="fill" /> Stop ({elapsed}s)
          <span className="ml-1 inline-block w-2 h-2 rounded-full bg-[#121212] animate-pulse" />
        </button>
      )}
      {busy && (
        <button disabled className="bg-[#5C5C5C] text-white rounded-full px-5 py-3 flex items-center gap-2 opacity-80">
          <CircleNotch size={18} className="animate-spin" /> Transkribiere …
        </button>
      )}
      {recording && (
        <span className="text-xs text-zinc-400">Sprich klar in Richtung Mikro. Stop, sobald fertig.</span>
      )}
    </div>
  );
}

export default function Journal() {
  const { user } = useAuth();
  const [entries, setEntries] = useState(null);
  const [text, setText] = useState("");
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setEntries(await listJournalEntries()); }
    catch { setEntries([]); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (text.trim().length < 2) { toast.error("Text fehlt"); return; }
    setSaving(true);
    try {
      const tagList = tags.split(",").map((t) => t.trim()).filter(Boolean);
      await createJournalEntry({ text: text.trim(), tags: tagList });
      toast.success("Gespeichert");
      setText(""); setTags("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Konnte nicht speichern");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eintrag löschen?")) return;
    await deleteJournalEntry(id);
    toast.success("Gelöscht");
    load();
  };

  if (user?.is_school) {
    return (
      <div className="eid-card p-6" data-testid="journal-school-blocked">
        <div className="font-medium">Sprach-Memo ist im Schulmodus deaktiviert</div>
        <div className="text-sm text-zinc-400 mt-1">
          Aus Datenschutz-Gründen (Tonaufnahmen Minderjähriger) kann diese Funktion
          nur von Hof-Konten genutzt werden.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="journal-page">
      <Helmet><title>Feld-Tagebuch · bodenblick.com</title></Helmet>

      <div className="eid-card p-6 md:p-8" data-testid="journal-hero"
        style={{ background: "linear-gradient(135deg, #141414 0%, #0E1A14 100%)" }}>
        <div className="flex items-center gap-2"><BookBookmark size={20} className="text-[#00FA9A]" weight="fill" /><div className="eid-label">Feld-Tagebuch</div></div>
        <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight max-w-2xl">
          Reinreden statt Tippen.
        </h1>
        <p className="mt-3 text-sm md:text-base text-zinc-400 max-w-2xl leading-relaxed">
          Auf dem Traktor schnell ins Mikro: <em>"Heute Unkraut zwischen Reihe 4 und 8, morgen
          früh nachfräsen."</em> — Wir verwandeln deine Stimme in Text (OpenAI Whisper),
          du sicherst sie als Notiz. Audio wird sofort verworfen, nur dein Transkript bleibt.
        </p>
      </div>

      <div className="eid-card p-5 space-y-3" data-testid="journal-compose">
        <Recorder onTranscribed={(t) => setText((cur) => (cur ? cur + " " + t : t))} />
        <textarea
          value={text} onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder="Notiz tippen oder oben ins Mikro sprechen …"
          data-testid="journal-text"
          className="w-full border border-white/10 rounded-sm px-3 py-2 text-sm"
        />
        <div className="flex items-center gap-2">
          <input
            value={tags} onChange={(e) => setTags(e.target.value)}
            placeholder="Tags (Komma-getrennt: unkraut, weizen, feld-3)"
            data-testid="journal-tags"
            className="flex-1 border border-white/10 rounded-sm px-3 py-2 text-sm"
          />
          <button
            onClick={save}
            disabled={saving || !text.trim()}
            data-testid="journal-save"
            className="bg-[#00FA9A] text-black hover:bg-[#33FBAE] disabled:opacity-50 rounded-full px-4 py-2 text-sm flex items-center gap-1"
          >
            {saving ? <CircleNotch size={12} className="animate-spin" /> : <PaperPlaneRight size={12} />}
            Speichern
          </button>
        </div>
      </div>

      <div className="eid-card overflow-hidden" data-testid="journal-list">
        <div className="px-4 py-3 border-b border-white/10 eid-label flex items-center justify-between">
          <span>Deine Einträge</span>
          {entries && <span className="font-mono">{entries.length}</span>}
        </div>
        {entries === null && (
          <div className="p-6 text-center text-sm text-zinc-500"><CircleNotch size={14} className="animate-spin inline" /> Lade …</div>
        )}
        {entries && entries.length === 0 && (
          <div className="p-8 text-center text-sm text-zinc-400" data-testid="journal-empty">
            Noch keine Einträge — sprich oben einfach drauf los.
          </div>
        )}
        {entries && entries.length > 0 && (
          <ul className="divide-y divide-white/10">
            {entries.map((e) => (
              <li key={e.id} className="p-4 flex items-start gap-3" data-testid={`journal-entry-${e.id}`}>
                <Pencil size={14} className="text-[#00FA9A] shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{e.text}</p>
                  <div className="mt-1 flex items-center gap-3 text-[11px] text-zinc-500">
                    <span className="inline-flex items-center gap-1"><Calendar size={10} />{fmtDate(e.created_at)}</span>
                    {(e.tags || []).map((t) => (
                      <span key={t} className="bg-white/5 text-zinc-400 px-1.5 py-0.5 rounded-sm">#{t}</span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => remove(e.id)}
                  data-testid={`journal-entry-delete-${e.id}`}
                  className="text-[#FF7A45] hover:bg-[#FF5500]/10 rounded p-1"
                  aria-label="Löschen"
                >
                  <Trash size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="eid-card p-4 text-xs text-zinc-400 flex items-start gap-2">
        <Info size={12} className="text-[#00FA9A] shrink-0 mt-0.5" />
        <span>
          <strong>Datenschutz:</strong> Audio wird zur Erkennung an OpenAI Whisper geschickt
          und sofort danach gelöscht. Wir speichern niemals die Aufnahme — nur das
          Transkript bleibt in deinem Konto.
        </span>
      </div>
    </div>
  );
}
